import { MonthlyGroupDataType } from "../../../../stores/types/campaignsTypes.tsx";
import {
  CellContext,
  ColumnDef,
  ColumnPinningState,
} from "@tanstack/react-table";
import { useState } from "react";
import { fullFormatNumbers } from "../../../../helpers/functions/formatNumber.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons";
import { RevMediaPlanTable } from "../table/RevMediaPlanTable.tsx";
import { MediaPlanMonthlySubComponent } from "../table/components/MediaPlanMonthlySubComponent.tsx";
import { RevInput } from "@revmatics/catalyst";

type Props = {
  monthly: MonthlyGroupDataType[];
  monthList: string[];
  edit: boolean;
};

export const MonthlyDetailsView = ({ monthly, monthList, edit }: Props) => {
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ["expander"],
  });
  const columns: ColumnDef<MonthlyGroupDataType>[] = [
    {
      id: "channels",
      header: () => <div className="w-full text-center">Channels</div>,
      columns:
        monthly.length > 0
          ? [
              {
                id: "expander",
                size: 206,
                header: () => null,
                cell: ({ row }) => {
                  return (
                    row.getCanExpand() &&
                    (row.original.sub_groups ? (
                      <button
                        {...{
                          onClick: row.getToggleExpandedHandler(),
                        }}
                        className="cursor-pointer grid grid-cols-[32px_1fr] w-[174px]"
                      >
                        <div className="flex items-center h-full">
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className={`text-md text-monochrome-600 ${row.getIsExpanded() ? "rotate-90" : ""}`}
                          />
                        </div>
                        <div className="gap-y-0.5 flex flex-col items-start">
                          <p className="typography-body-sm text-monochrome-900">
                            {row.original.name}
                          </p>
                          <p className="typography-body-xs text-monochrome-500 whitespace-nowrap text-ellipsis overflow-hidden max-w-[135px]">
                            {row.original.sub_groups
                              .map((sub) => sub.name)
                              .join(", ")}
                          </p>
                        </div>
                      </button>
                    ) : (
                      <div className="cursor-pointer grid grid-cols-[32px_1fr]">
                        <div />
                        <p className="typography-body-sm text-monochrome-900">
                          {row.original.name}
                        </p>
                      </div>
                    ))
                  );
                },
              },
            ]
          : undefined,
    },
    {
      header: "Months",
      columns:
        monthly.length > 0
          ? [
              {
                accessorKey: "total",
                id: "total",
                header: () => (
                  <div className="w-full text-end min-w-[118px]">Total</div>
                ),
                cell: ({ row }) => {
                  const originalData = row.original;
                  return (
                    <div className="gap-y-0.5 flex flex-col items-end">
                      <p className="typography-body-sm text-monochrome-900">
                        $
                        {fullFormatNumbers({
                          value: originalData.total.amount,
                          noFormatNumber: true,
                          shortenValue: 1e3,
                        })}
                      </p>
                      {edit &&
                      originalData.total.percentage !== 100 &&
                      !originalData.sub_groups ? (
                        <RevInput
                          name={`monthly-${originalData.id}-${originalData.name}`}
                          type="number"
                          label=""
                          size="sm"
                          afterText="%"
                          defaultValue={originalData.total.percentage.toFixed(
                            2,
                          )}
                          inputClassName="text-end"
                        />
                      ) : (
                        <p className="typography-body-xs text-monochrome-500">
                          {originalData.total.percentage.toFixed(2)}%
                        </p>
                      )}
                    </div>
                  );
                },
              },
              ...monthList.map((month) => {
                return {
                  accessorKey: month,
                  id: month,
                  header: () => (
                    <div className="w-full text-end capitalize min-w-[118px]">
                      {month}
                    </div>
                  ),
                  cell: ({
                    row,
                  }: CellContext<MonthlyGroupDataType, unknown>) => {
                    const originalData = row.original;
                    return (
                      <div className="gap-y-0.5 flex flex-col items-end">
                        <p className="typography-body-sm text-monochrome-900">
                          $
                          {fullFormatNumbers({
                            value: originalData.months[month].amount,
                            noFormatNumber: true,
                            shortenValue: 1e3,
                          })}
                        </p>
                        {edit && !originalData.sub_groups ? (
                          <RevInput
                            name={`monthly-${originalData.id}-${originalData.name}`}
                            type="number"
                            label=""
                            size="sm"
                            afterText="%"
                            defaultValue={originalData.months[
                              month
                            ].percentage.toFixed(2)}
                            inputClassName="text-end"
                          />
                        ) : (
                          <p className="typography-body-xs text-monochrome-500">
                            {originalData.months[month].percentage.toFixed(2)}%
                          </p>
                        )}
                      </div>
                    );
                  },
                };
              }),
            ]
          : undefined,
    },
  ];
  return (
    <RevMediaPlanTable<MonthlyGroupDataType>
      data={monthly || []}
      columns={columns}
      getRowCanExpand={() => true}
      renderSubComponent={MediaPlanMonthlySubComponent}
      edit={edit}
      columnPinning={columnPinning}
      onColumnPinningChange={setColumnPinning}
    />
  );
};
